import { useClientPortalAPI, useClientPortalSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { PrimaryButton, Stack, Text } from "@certn/cascadia";
import {
  Container,
  Divider,
  Layout,
  LinkButton,
  Loader,
  SecondaryTopNav,
  SectionMessage,
  showToast,
  TagGroupSelect,
  TagGroupSelectFormikField,
  TextAreaFormikField,
  TextInputFormikField,
  Toggle,
} from "@certn/cascadia-react";
import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { breakpoints } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { Formik } from "formik";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { array, mixed, object, string } from "yup";

import { AdjudicationBetaBadge } from "../../components/adjudication-beta/AdjudicationBetaBadge";
import {
  RubricSelectionRadioGroup,
  RubricSelectionValue,
} from "../../components/adjudication-rubric-selection/RubricSelectionRadioGroup";
import { CollapsibleSection } from "../../components/collapsible-section/CollapsibleSection";
import { OrderSummary } from "../../components/order-summary/OrderSummary";
import { OrderingCheckCard } from "../../components/ordering-check-card";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useCanViewClientPortalPricing } from "../../components/permissions/Permissions";
import { usePermissions } from "../../components/permissions/PermissionsProvider";
import i18n from "../../i18n";
import { formatNullableCurrency } from "../../utils/formatNullableCurrency";
import {
  CheckInBundleConflict,
  findStandaloneChecksConflictingWithBundles,
} from "./bundle-conflict-utils";
import { CheckInBundleConflictModal } from "./CheckInBundleConflictModal";
import { useDuplicatePackage } from "./hooks";
import { SelectExistingRubricSection } from "./package-creation/components/steps/components/SelectExistingRubricSection";
import {
  applyPackageChannelToggle,
  calculatePackageReviewPrice,
  getMissingClientInputClaimsMessage,
  PackageChannelFlag,
  packageContextToAPI,
  shouldShowClientInputConfiguration,
} from "./package-creation/package-utils";
import { Package, PackageContext } from "./package-creation/PackageContext";
import { PackageATSConfiguration } from "./PackageATSConfiguration";
import {
  filterPackageChecksExcludingBundles,
  packageDetailToPackageContext,
} from "./package-edit-utils";
import {
  packageCreatePermissions,
  packageUpdatePermissions,
} from "./package-permissions";
import { PackageEditBundlesSection } from "./PackageEditBundlesSection";
import { PackageEditChecksSection } from "./PackageEditChecksSection";
import { PackageDetailResponse } from "./types";

type PackageFullUpdatePayload = components["schemas"]["PackageCreate"] & {
  adjudication_rubric_id: string | null;
};

export function PackageDetails() {
  const { enableClientAdjudication, packageImprovements } = useFlags();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const { packageId } = useParams();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(
    `(min-width: ${breakpoints["breakpoint-lg"].maxWidth}px)`
  );
  const [searchParams] = useSearchParams();
  const {
    data: packageData,
    isLoading,
    mutate,
    error: packageError,
  } = useClientPortalAPI(`/api/internal/client-portal/packages/${packageId}/`);
  const [formattedPrice, setFormattedPrice] = useState<string | null>(null);
  const [rubricSelection, setRubricSelection] =
    useState<RubricSelectionValue>("USE_DEFAULT");
  const [editablePackage, setEditablePackage] = useState<Package | null>(null);
  const [checkInBundleConflict, setCheckInBundleConflict] =
    useState<CheckInBundleConflict | null>(null);
  const [isAtsFormSubmitted, setIsAtsFormSubmitted] = useState(false);

  const caseCount = packageData?.case_count ?? 0;
  const hasUpdatePermission = hasPermission(packageUpdatePermissions);
  const isViewOnly =
    !!packageImprovements &&
    (searchParams.get("view") === "true" ||
      caseCount > 0 ||
      !hasUpdatePermission);
  const showCasesViewOnlyMessage = packageImprovements && caseCount > 0;
  const canFullyEdit =
    !!packageImprovements &&
    !isViewOnly &&
    caseCount === 0 &&
    hasUpdatePermission;
  const canDuplicatePackage =
    packageImprovements && hasPermission(packageCreatePermissions);
  const { duplicatePackage, isDuplicating } = useDuplicatePackage();
  const shouldFetchBundles =
    packageImprovements &&
    (canFullyEdit || (packageData?.bundles?.length ?? 0) > 0);
  const {
    data: availableBundles,
    isLoading: isBundlesLoading,
    error: bundlesError,
  } = useClientPortalAPI(
    shouldFetchBundles
      ? `/api/internal/client-portal/customer-accounts/available-bundles/`
      : null
  );
  const {
    data: availableChecks,
    isLoading: isChecksLoading,
    error: checksError,
  } = useClientPortalAPI(
    canFullyEdit
      ? `/api/internal/client-portal/customer-accounts/available-checks/`
      : null
  );
  const { data: tags } = useClientPortalAPI(
    canFullyEdit ? `/api/internal/client-portal/tags/active/` : null
  );

  useEffect(() => {
    if (packageData) {
      const price = formatNullableCurrency(packageData?.price.price, {
        currency: packageData.price.currency,
        locale: i18n.language,
        fallback: null,
      });
      setFormattedPrice(price);

      if (packageData.adjudication_rubric?.id) {
        setRubricSelection("SELECT_EXISTING");
      } else {
        setRubricSelection("USE_DEFAULT");
      }
    }
  }, [packageData]);

  useEffect(() => {
    if (
      !canFullyEdit ||
      !packageData ||
      !availableBundles ||
      isBundlesLoading ||
      isChecksLoading ||
      editablePackage
    ) {
      return;
    }

    setEditablePackage(
      packageDetailToPackageContext(
        packageData as PackageDetailResponse,
        availableBundles,
        availableChecks || []
      )
    );
  }, [
    canFullyEdit,
    packageData,
    availableBundles,
    isBundlesLoading,
    availableChecks,
    isChecksLoading,
    editablePackage,
  ]);

  const rubricResponse = useClientPortalAPI(
    packageData
      ? "/api/internal/client-portal/adjudication-rubrics/filter-by-check-type/"
      : null,
    {
      check_type: packageData?.checks.map((check) =>
        check.selected_option &&
        check.selected_option.ordering_type === "EXPLICIT_ORDERING"
          ? check.selected_option.value
          : check.type
      ),
    }
  );

  const viewOnlyBundleTitles = useMemo(() => {
    if (!packageData?.bundles?.length) {
      return [];
    }
    const catalog = new Map(
      (availableBundles || []).map((bundle) => [
        String(bundle.id),
        bundle.title,
      ])
    );
    return packageData.bundles.map(
      (bundleId) => catalog.get(String(bundleId)) ?? String(bundleId)
    );
  }, [packageData?.bundles, availableBundles]);

  const viewOnlyChecks = useMemo(() => {
    if (!packageData?.checks?.length) {
      return [];
    }

    if (!packageData.bundles?.length) {
      return packageData.checks;
    }

    return filterPackageChecksExcludingBundles(
      packageData.checks,
      packageData.bundles,
      availableBundles || []
    );
  }, [packageData?.checks, packageData?.bundles, availableBundles]);

  const displayPrice = useMemo(() => {
    if (
      packageImprovements &&
      canFullyEdit &&
      editablePackage &&
      packageData?.price?.currency
    ) {
      const { price, currency } = calculatePackageReviewPrice(
        editablePackage,
        packageData.price.currency as
          | "USD"
          | "EUR"
          | "GBP"
          | "AUD"
          | "CAD"
          | "NZD"
      );
      return formatNullableCurrency(price, {
        currency,
        locale: i18n.language,
        fallback: null,
      });
    }

    return formattedPrice;
  }, [
    packageImprovements,
    canFullyEdit,
    editablePackage,
    packageData?.price?.currency,
    formattedPrice,
  ]);

  const tagsList = useMemo(() => {
    const combined = (tags || []).concat(packageData?.tags || []);
    return [...new Map(combined.map((tag) => [tag.id, tag])).values()];
  }, [tags, packageData?.tags]);

  const getEditablePackageAtsErrors = (
    pkg: Package,
    clientForm?: { data?: Record<string, unknown>; errors?: unknown[] }
  ): string[] => {
    if (
      (!pkg.is_ats_package && !pkg.is_api_package) ||
      !shouldShowClientInputConfiguration(pkg)
    ) {
      return [];
    }

    const claims = clientForm?.data ?? pkg.client_input_claims;
    const formErrors = clientForm?.errors ?? [];
    if (formErrors.length > 0 || !claims || Object.keys(claims).length === 0) {
      return [getMissingClientInputClaimsMessage(t)];
    }

    return [];
  };

  const handleChannelToggle = (
    channel: PackageChannelFlag,
    selected: boolean,
    formClaims?: Record<string, unknown>
  ) => {
    if (!editablePackage) return;
    const hasFormClaims = formClaims && Object.keys(formClaims).length > 0;
    setEditablePackage({
      ...editablePackage,
      ...applyPackageChannelToggle(channel, selected),
      client_input_claims: hasFormClaims
        ? formClaims
        : editablePackage.client_input_claims,
    });
  };

  const initialValues = {
    name: packageData?.name || "",
    description: packageData?.description || "",
    rubric_id: packageData?.adjudication_rubric?.id || null,
    tag_names: packageData?.tags || [],
    clientFormDataAndErrors: {
      data:
        (packageData?.client_input_claims as
          | Record<string, unknown>
          | undefined) || {},
      errors: [] as unknown[],
    },
  };

  const validationSchema = object().shape({
    name: string().required(t("Name is required")),
    description: string(),
    rubric_id: string().nullable(),
    tag_names: array(),
    clientFormDataAndErrors: mixed(),
  });

  const { submit } = useClientPortalSubmit(
    `/api/internal/client-portal/packages/${packageId}/update/`,
    {
      throwErrors: false,
      showToastOnError: true,
      onSuccess: async () => {
        try {
          const refreshedPackage = await mutate();
          if (refreshedPackage) {
            setEditablePackage(
              packageDetailToPackageContext(
                refreshedPackage as PackageDetailResponse,
                availableBundles || [],
                availableChecks || []
              )
            );
          }
        } catch {
          // Best-effort refresh after save; primary save already succeeded.
        }
        showToast(t("Package saved"), { type: "success" });
        if (packageImprovements) {
          navigate("/packages");
        }
      },
    }
  );

  if (packageError) {
    return (
      <SectionMessage
        type="danger"
        icon="error"
        sectionTitle={t("Unable to load package")}
      >
        {String(packageError.message)}
      </SectionMessage>
    );
  }

  if (canFullyEdit && (bundlesError || checksError)) {
    return (
      <SectionMessage
        type="danger"
        icon="error"
        sectionTitle={t("Unable to load package edit data")}
      >
        {String((bundlesError || checksError)?.message)}
      </SectionMessage>
    );
  }

  if (
    isLoading ||
    (shouldFetchBundles && isBundlesLoading) ||
    (canFullyEdit && (!editablePackage || isChecksLoading))
  ) {
    return <Loader type="dots" />;
  }

  const noopStep = () => undefined;

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => {
          const rubricId =
            rubricSelection === "USE_DEFAULT" ? null : values.rubric_id;

          if (canFullyEdit) {
            if (!editablePackage) {
              showToast(t("Package contents are still loading. Try again."), {
                type: "error",
              });
              return;
            }

            const bundleCheckConflicts =
              findStandaloneChecksConflictingWithBundles(
                editablePackage.checks,
                editablePackage.bundles
              );
            if (bundleCheckConflicts.length > 0) {
              setCheckInBundleConflict(bundleCheckConflicts[0]);
              return;
            }

            const clientForm = values.clientFormDataAndErrors;
            const atsErrors = getEditablePackageAtsErrors(editablePackage, {
              data: clientForm?.data,
              errors: clientForm?.errors,
            });
            if (atsErrors.length > 0) {
              setIsAtsFormSubmitted(true);
              showToast(atsErrors[0], { type: "error" });
              return;
            }

            await submit({
              ...packageContextToAPI({
                ...editablePackage,
                name: values.name,
                description: values.description,
                tag_names: values.tag_names,
                rubric_id: rubricId,
                client_input_claims: clientForm?.data || {},
              }),
              adjudication_rubric_id: rubricId,
            } satisfies PackageFullUpdatePayload);
            return;
          }

          await submit({
            name: values.name,
            description: values.description,
            adjudication_rubric_id: rubricId,
          });
        }}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({ values, isSubmitting, isValid, handleSubmit }) => (
          <Layout
            header={
              <SecondaryTopNav
                testID="package-details-top-nav"
                leftIcon="close"
                onleftIconOnPress={() => {
                  navigate(-1);
                }}
              >
                {!isViewOnly && (
                  <PrimaryButton
                    onPress={() => {
                      if (isValid) {
                        handleSubmit();
                      }
                    }}
                    isDisabled={
                      isSubmitting ||
                      !isValid ||
                      (canFullyEdit &&
                        editablePackage !== null &&
                        editablePackage.checks.length === 0 &&
                        editablePackage.bundles.length === 0)
                    }
                  >
                    <Trans>Save changes</Trans>
                  </PrimaryButton>
                )}
              </SecondaryTopNav>
            }
            layout="narrow"
            rightSideBar={
              canFullyEdit &&
              editablePackage &&
              canViewClientPortalPricing &&
              isDesktop ? (
                <OrderSummary
                  checks={editablePackage.checks}
                  bundles={editablePackage.bundles}
                  title={t("Package summary")}
                />
              ) : undefined
            }
            testID="package-details-container"
          >
            <Container
              padding={["space-3"]}
              layout="narrow"
              testID="apply-link-details-container"
            >
              <PageTitle>
                {isViewOnly ? t("View package") : t("Package details")}
              </PageTitle>
              {isViewOnly && showCasesViewOnlyMessage && (
                <SectionMessage type="info" icon="info">
                  {canDuplicatePackage ? (
                    <>
                      {t(
                        "This package has associated cases and can only be viewed."
                      )}{" "}
                      <LinkButton
                        testID="package-duplicate-link-button"
                        linkStyle="monochrome"
                        textStyle="global/body-secondary"
                        onPress={() => {
                          if (packageId) {
                            void duplicatePackage(packageId);
                          }
                        }}
                        isDisabled={isDuplicating}
                      >
                        {t("Duplicate this package")}
                      </LinkButton>{" "}
                      {t("to make changes.")}
                    </>
                  ) : (
                    t(
                      "This package has associated cases and can only be viewed. Create a duplicate to make changes."
                    )
                  )}
                </SectionMessage>
              )}
              <div className={styles.topFieldGroup}>
                <TextInputFormikField
                  name="name"
                  label={t("Package name")}
                  testID="package-details-name-field"
                  isRequired
                  value={values.name}
                  isDisabled={isViewOnly}
                />
                <TextAreaFormikField
                  name="description"
                  label={t("Package description")}
                  description={t(
                    "This will only be seen by your team and is not viewable to the applicant."
                  )}
                  maxLength={120}
                  testID="package-details-description-field"
                  isDisabled={isViewOnly}
                />
                {canFullyEdit && editablePackage ? (
                  <>
                    <Text style="form/body-secondary">
                      {t("Ordering through an ATS or the API? Choose one.")}
                    </Text>
                    <Toggle
                      testID="ats-package-toggle"
                      isSelected={Boolean(editablePackage.is_ats_package)}
                      description={t(
                        "Orders come from a connected ATS integration."
                      )}
                      onChange={(selected) =>
                        handleChannelToggle(
                          "is_ats_package",
                          selected,
                          values.clientFormDataAndErrors?.data
                        )
                      }
                    >
                      {t("Mark this package for ATS use")}
                    </Toggle>
                    <Toggle
                      testID="api-package-toggle"
                      isSelected={Boolean(editablePackage.is_api_package)}
                      description={t(
                        "Orders come through the Certn API. Settings saved here fill in anything the order doesn't send."
                      )}
                      onChange={(selected) =>
                        handleChannelToggle(
                          "is_api_package",
                          selected,
                          values.clientFormDataAndErrors?.data
                        )
                      }
                    >
                      {t("Mark this package for API use")}
                    </Toggle>
                  </>
                ) : (
                  <>
                    {packageData?.is_ats_package && (
                      <Text style="form/body-primary">
                        {t("This package is configured for ATS integrations.")}
                      </Text>
                    )}
                    {packageData?.is_api_package && (
                      <Text style="form/body-primary">
                        {t("This package is configured for API orders.")}
                      </Text>
                    )}
                  </>
                )}
                {canFullyEdit &&
                  editablePackage &&
                  shouldShowClientInputConfiguration(editablePackage) && (
                    <PackageATSConfiguration
                      packageData={editablePackage}
                      isSubmitted={isAtsFormSubmitted}
                    />
                  )}
                {canFullyEdit ? (
                  <TagGroupSelectFormikField
                    testID="package-details-tags-field"
                    label={t("Tags")}
                    name="tag_names"
                    values={tagsList}
                    selectedValues={values.tag_names}
                    setSelectedValues={() => undefined}
                    description={t(
                      "Tags applied to this package will be automatically be added to each case."
                    )}
                  />
                ) : (
                  <TagGroupSelect
                    testID="package-details-tags-field"
                    label={t("Tags")}
                    values={packageData?.tags || []}
                    selectedValues={packageData?.tags || []}
                    setSelectedValues={function (): void {
                      throw new Error("Function not implemented.");
                    }}
                    isDisabled
                  />
                )}
              </div>
              {enableClientAdjudication && (
                <>
                  <Divider />
                  <div className={styles.rubricSection}>
                    <Stack gap="space-2" orientation="horizontal">
                      <Text style="form/section-heading">
                        {t("Scoring rules")}
                      </Text>
                      <AdjudicationBetaBadge />
                    </Stack>
                    {canFullyEdit ? (
                      <>
                        <Text style="form/body-primary">
                          {t(
                            "Scoring rules determine how package results are evaluated. You can use default scoring or apply a rubric (a saved set of scoring rules) to customise outcomes."
                          )}
                        </Text>
                        <RubricSelectionRadioGroup
                          value={rubricSelection}
                          onChange={setRubricSelection}
                        />
                        {rubricSelection === "SELECT_EXISTING" && (
                          <SelectExistingRubricSection
                            response={rubricResponse}
                            rubric_id={values.rubric_id}
                          />
                        )}
                      </>
                    ) : (
                      <Text style="form/body-primary">
                        {packageData?.adjudication_rubric?.name ??
                          t("Use default scoring")}
                      </Text>
                    )}
                  </div>
                  <Divider />
                </>
              )}
              <div>
                <div className={styles.disclaimerContainer}>
                  <div className={styles.price}>
                    <Text style="form/section-heading">
                      {t("Package contents")}
                    </Text>
                    {canViewClientPortalPricing && displayPrice && (
                      <Text
                        style="global/heading-small"
                        color="text-accent-tofino"
                      >
                        {`${t("Price")} ${displayPrice}`}
                      </Text>
                    )}
                  </div>
                  {canFullyEdit && (
                    <Text style="form/body-secondary">
                      {t(
                        "Bundled deals include their own checks. Add individual checks below for anything not covered by a bundle."
                      )}
                    </Text>
                  )}
                </div>
                {canFullyEdit && editablePackage ? (
                  <PackageContext.Provider
                    value={{
                      packageData: editablePackage,
                      setPackage: setEditablePackage,
                      setClientInputClaims: (claims) => {
                        setEditablePackage({
                          ...editablePackage,
                          client_input_claims: claims,
                        });
                      },
                      goToNextStep: noopStep,
                      goToPrevStep: noopStep,
                      currentStep: 0,
                    }}
                  >
                    <div className={styles.editableChecks}>
                      {!isDesktop && canViewClientPortalPricing && (
                        <CollapsibleSection
                          data={
                            <OrderSummary
                              checks={editablePackage.checks}
                              bundles={editablePackage.bundles}
                            />
                          }
                          title={t("Package summary")}
                          testID="package-summary-toggle"
                        />
                      )}
                      <PackageEditBundlesSection />
                      <div className={styles.individualChecks}>
                        <Text style="global/heading-medium">
                          {t("Individual checks")}
                        </Text>
                        <PackageEditChecksSection />
                      </div>
                    </div>
                  </PackageContext.Provider>
                ) : (
                  <div className={styles.checkCards}>
                    {viewOnlyBundleTitles.map((bundleTitle) => (
                      <Text
                        key={bundleTitle}
                        style="form/body-primary"
                        testID={`package-details-bundle-${bundleTitle}`}
                      >
                        {bundleTitle}
                      </Text>
                    ))}
                    {viewOnlyChecks.map((check: any) => {
                      return (
                        <OrderingCheckCard
                          name={check.name}
                          type={check.type}
                          turnaroundTime={check.estimated_turnaround_time}
                          disclaimersIconList={check.disclaimers_icon_list}
                          disclaimers={check.disclaimers}
                          description={check.description}
                          infoParagraphs={check.info_paragraphs}
                          coverage={check.coverage}
                          umbrellaChecksSupported={[]}
                          testID={`package-details-${check.checktype}-check-card`}
                          isReadOnly
                          selectedArgument={check.selected_option}
                          selectedOptions={
                            check.selected_options || check.selectedOptions
                          }
                          key={check.name}
                          selectFieldInformation={
                            check.select_field_information
                          }
                          addOnOptions={check.add_on_options}
                          checkArguments={check.check_arguments}
                          currency={
                            check.currency || packageData?.price?.currency
                          }
                          isDisabled
                          isSelected
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </Container>
          </Layout>
        )}
      </Formik>
      {checkInBundleConflict && (
        <CheckInBundleConflictModal
          isOpen
          conflict={checkInBundleConflict}
          onClose={() => setCheckInBundleConflict(null)}
        />
      )}
    </>
  );
}

const styles = {
  topFieldGroup: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-5)",
  }),
  disclaimerContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
    padding: "15px 0 15px 0",
  }),
  price: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
  }),
  checkCards: css({
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    paddingTop: "15px",
  }),
  editableChecks: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-8)",
    paddingTop: "var(--space-4)",
  }),
  individualChecks: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
  rubricSection: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-5)",
  }),
};
